import { supabase } from './supabase';
import type { Product, Category, CustomizationField, ProductVariation, VariationImage } from '@jigflow/shared';

export async function getProducts(workspaceId: string): Promise<Product[]> {
  const { data: productsData, error: productsError } = await supabase
    .from('products')
    .select('*, categories(name)')
    .eq('workspace_id', workspaceId)
    .eq('is_draft', false)
    .order('created_at', { ascending: true });

  if (productsError) throw productsError;
  if (!productsData) return [];

  return productsData.map((row) => ({
    id: row.id,
    name: row.name,
    description: row.description || '',
    price: parseFloat(row.price),
    image: row.image_url || '',
    images: row.images ? (Array.isArray(row.images) ? row.images : []) : [],
    category: row.category_id || '',
    width: 0,
    height: 0,
    fields: [],
    isDraft: row.is_draft,
  }));
}

export async function getProduct(id: string): Promise<Product | null> {
  const { data, error } = await supabase
    .from('products')
    .select('*, categories(name)')
    .eq('id', id)
    .single();

  if (error || !data) return null;

  return {
    id: data.id,
    name: data.name,
    description: data.description || '',
    price: parseFloat(data.price),
    image: data.image_url || '',
    images: data.images ? (Array.isArray(data.images) ? data.images : []) : [],
    category: data.category_id || '',
    width: 0,
    height: 0,
    fields: [],
    isDraft: data.is_draft,
  };
}

export async function getCategories(workspaceId: string): Promise<Category[]> {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('workspace_id', workspaceId);

  if (error) throw error;
  if (!data) return [];

  return data.map((row) => ({
    id: row.id,
    name: row.name,
  }));
}

export async function getFieldsByProductId(productId: string): Promise<CustomizationField[]> {
  const { data, error } = await supabase
    .from('customization_fields')
    .select('*')
    .eq('product_id', productId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  if (!data) return [];

  return data.map((row) => ({
    id: row.id,
    productId: row.product_id,
    label: row.label,
    type: row.type,
    required: row.required || false,
    options: row.options ? (Array.isArray(row.options) ? row.options : []) : undefined,
    maxLength: row.max_length || undefined,
    orderIndex: row.order_index,
    score: row.score || false,
    engrave: row.engrave || false,
    fontFamily: row.font_family || undefined,
  }));
}

export async function getVariationsByProductId(productId: string): Promise<ProductVariation[]> {
  const { data, error } = await supabase
    .from('product_variations')
    .select('*')
    .eq('product_id', productId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  if (!data) return [];

  return data.map((row) => ({
    id: row.id,
    productId: row.product_id,
    name: row.name,
    orderIndex: row.sort_order,
  }));
}

export async function getVariationImages(variationId: string): Promise<VariationImage[]> {
  const { data, error } = await supabase
    .from('product_variations')
    .select('image_url')
    .eq('id', variationId)
    .single();

  if (error || !data || !data.image_url) return [];

  return [{
    id: variationId,
    variationId: variationId,
    image: data.image_url,
    isPrimary: true,
    orderIndex: 0,
  }];
}

export async function createProduct(workspaceId: string, data: Partial<Product>): Promise<Product> {
  const { data: created, error } = await supabase
    .from('products')
    .insert({
      workspace_id: workspaceId,
      name: data.name || '',
      description: data.description,
      price: data.price || 0,
      image_url: data.image,
      images: data.images,
      category_id: data.category || null,
      is_draft: data.isDraft || false,
    })
    .select('*, categories(name)')
    .single();

  if (error) throw error;

  return {
    id: created.id,
    name: created.name,
    description: created.description || '',
    price: parseFloat(created.price),
    image: created.image_url || '',
    images: created.images ? (Array.isArray(created.images) ? created.images : []) : [],
    category: created.category_id || '',
    width: 0,
    height: 0,
    fields: [],
    isDraft: created.is_draft,
  };
}

export async function updateProduct(id: string, data: Partial<Product>): Promise<Product> {
  const updateData: Record<string, any> = {};
  if (data.name !== undefined) updateData.name = data.name;
  if (data.description !== undefined) updateData.description = data.description;
  if (data.price !== undefined) updateData.price = data.price;
  if (data.image !== undefined) updateData.image_url = data.image;
  if (data.images !== undefined) updateData.images = data.images;
  if (data.category !== undefined) updateData.category_id = data.category || null;
  if (data.isDraft !== undefined) updateData.is_draft = data.isDraft;

  const { data: updated, error } = await supabase
    .from('products')
    .update(updateData)
    .eq('id', id)
    .select('*, categories(name)')
    .single();

  if (error) throw error;

  return {
    id: updated.id,
    name: updated.name,
    description: updated.description || '',
    price: parseFloat(updated.price),
    image: updated.image_url || '',
    images: updated.images ? (Array.isArray(updated.images) ? updated.images : []) : [],
    category: updated.category_id || '',
    width: 0,
    height: 0,
    fields: [],
    isDraft: updated.is_draft,
  };
}

export async function deleteProduct(id: string): Promise<void> {
  const { error } = await supabase
    .from('products')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Image upload to Supabase Storage
export async function uploadProductImage(
  workspaceId: string,
  productId: string,
  file: File
): Promise<string> {
  const imageId = crypto.randomUUID();
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const storagePath = `${workspaceId}/${productId}/${imageId}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('product-images')
    .upload(storagePath, file, {
      contentType: file.type || 'image/jpeg',
    });

  if (uploadError) throw uploadError;

  return getProductImageUrl(storagePath);
}

export function getProductImageUrl(storagePath: string): string {
  const { data } = supabase.storage.from('product-images').getPublicUrl(storagePath);
  return data.publicUrl;
}

export async function deleteProductImage(storagePath: string): Promise<void> {
  // Extract the path from a full URL if needed
  const path = storagePath.includes('product-images/')
    ? storagePath.split('product-images/').pop() || storagePath
    : storagePath;

  const { error } = await supabase.storage
    .from('product-images')
    .remove([path]);

  if (error) throw error;
}

export async function addField(
  workspaceId: string,
  field: Omit<CustomizationField, 'id'>
): Promise<CustomizationField> {
  const { data, error } = await supabase
    .from('customization_fields')
    .insert({
      workspace_id: workspaceId,
      product_id: field.productId,
      label: field.label,
      type: field.type,
      required: field.required,
      options: field.options || null,
      max_length: field.maxLength || null,
      order_index: field.orderIndex,
      score: field.score || false,
      engrave: field.engrave !== false, // default true
      font_family: field.fontFamily || 'Roboto',
    })
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    productId: data.product_id,
    label: data.label,
    type: data.type,
    required: data.required || false,
    options: data.options ? (Array.isArray(data.options) ? data.options : []) : undefined,
    maxLength: data.max_length || undefined,
    orderIndex: data.order_index,
    score: data.score || false,
    engrave: data.engrave || false,
    fontFamily: data.font_family || undefined,
  };
}

export async function updateField(field: CustomizationField): Promise<void> {
  const { error } = await supabase
    .from('customization_fields')
    .update({
      label: field.label,
      type: field.type,
      required: field.required,
      options: field.options || null,
      max_length: field.maxLength || null,
      order_index: field.orderIndex,
      score: field.score || false,
      engrave: field.engrave || false,
      font_family: field.fontFamily || null,
    })
    .eq('id', field.id);

  if (error) throw error;
}

export async function deleteField(fieldId: string): Promise<void> {
  const { error } = await supabase
    .from('customization_fields')
    .delete()
    .eq('id', fieldId);

  if (error) throw error;
}

export async function reorderFields(productId: string, fieldIds: string[]): Promise<void> {
  // Update each field's order_index based on its position in the array
  const updates = fieldIds.map((id, index) =>
    supabase
      .from('customization_fields')
      .update({ order_index: index })
      .eq('id', id)
      .eq('product_id', productId)
  );

  const results = await Promise.all(updates);
  const failed = results.find(r => r.error);
  if (failed?.error) throw failed.error;
}

// Variation CRUD
export async function createVariation(
  workspaceId: string,
  data: { productId: string; name: string; orderIndex: number }
): Promise<ProductVariation> {
  const { data: created, error } = await supabase
    .from('product_variations')
    .insert({
      workspace_id: workspaceId,
      product_id: data.productId,
      name: data.name,
      sort_order: data.orderIndex,
    })
    .select()
    .single();

  if (error) throw error;

  return {
    id: created.id,
    productId: created.product_id,
    name: created.name,
    orderIndex: created.sort_order,
  };
}

export async function updateVariation(
  id: string,
  data: Partial<{ name: string; orderIndex: number }>
): Promise<void> {
  const updateData: Record<string, unknown> = {};
  if (data.name !== undefined) updateData.name = data.name;
  if (data.orderIndex !== undefined) updateData.sort_order = data.orderIndex;

  const { error } = await supabase
    .from('product_variations')
    .update(updateData)
    .eq('id', id);

  if (error) throw error;
}

export async function deleteVariation(id: string): Promise<void> {
  const { error } = await supabase
    .from('product_variations')
    .delete()
    .eq('id', id);

  if (error) throw error;
}
